import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { hasAnyRole } from './useAuth';

import { bootstrapSession } from '@/shared/api/apolloClient';
import { SplashScreen } from '@/shared/ui/SplashScreen';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

interface RequireAuthProps {
  /** When set, only users with at least one of these role names are allowed in. */
  allowed_roles?: readonly string[];
  /** When set, every permission key listed here must be present. */
  required_permissions?: readonly string[];
  /** When set, at least one of these permission keys must be present. */
  any_permission?: readonly string[];
}

const APP_FALLBACK_ROUTES: readonly {
  path: string;
  any_permission: readonly string[];
}[] = [
  { path: '/app/dashboard', any_permission: ['admin.dashboard.view'] },
  { path: '/app/cashier', any_permission: ['pos.cashier.use'] },
  { path: '/app/sales-history', any_permission: ['pos.cashier.use'] },
  { path: '/app/doctor', any_permission: ['pos.doctor.use'] },
  { path: '/app/display/waiting-room', any_permission: ['display.waiting_room.view'] },
  { path: '/app/display/medical-queue', any_permission: ['display.medical_queue.view'] },
  { path: '/app/customers', any_permission: ['customers.read'] },
  { path: '/app/catalog', any_permission: ['catalog.products.read', 'catalog.services.read'] },
  { path: '/app/inventory', any_permission: ['inventory.read'] },
  {
    path: '/app/cash',
    any_permission: ['cash.shift.open', 'cash.movement.cash_in', 'pos.cashier.use'],
  },
  { path: '/app/open-accounts', any_permission: ['open_accounts.read'] },
  { path: '/app/invoicing', any_permission: ['invoicing.cfdi.request'] },
  { path: '/app/audit', any_permission: ['audit.view'] },
  { path: '/app/otp', any_permission: ['admin.users.manage'] },
];

function firstAllowedAppPath(permissions: readonly string[]): string | null {
  const route = APP_FALLBACK_ROUTES.find((item) =>
    item.any_permission.some((p) => permissions.includes(p)),
  );
  return route?.path ?? null;
}

function AccessDenied(): JSX.Element {
  return (
    <div className="text-muted-foreground border-border bg-card rounded-md border p-4 text-sm">
      No tienes permisos para ver esta sección.
    </div>
  );
}

/**
 * Auth gate. On first render, attempts a refresh-cookie bootstrap so a returning user
 * lands directly into the app. While that's in flight the gate shows the boot splash —
 * continuing the one already on screen from index.html rather than dropping to blank.
 */
export function RequireAuth({
  allowed_roles,
  required_permissions,
  any_permission,
}: RequireAuthProps): JSX.Element {
  const location = useLocation();
  const access_token = useAuthStore((s) => s.access_token);
  const user = useAuthStore((s) => s.user);
  const workstation_id = useWorkstationStore((s) => s.workstation_id);
  const [bootstrapped, setBootstrapped] = useState<boolean>(Boolean(access_token));

  useEffect(() => {
    if (access_token) {
      setBootstrapped(true);
      return;
    }
    let cancelled = false;
    void bootstrapSession().finally(() => {
      if (!cancelled) setBootstrapped(true);
    });
    return () => {
      cancelled = true;
    };
  }, [access_token]);

  if (!workstation_id) {
    return <Navigate to="/setup/workstation" replace state={{ from: location }} />;
  }

  if (!bootstrapped) {
    return <SplashScreen />;
  }

  if (!access_token || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowed_roles && !hasAnyRole(user.roles, allowed_roles)) {
    const fallback = firstAllowedAppPath(user.permissions);
    return fallback && fallback !== location.pathname ? (
      <Navigate to={fallback} replace />
    ) : (
      <AccessDenied />
    );
  }
  if (required_permissions && !required_permissions.every((p) => user.permissions.includes(p))) {
    const fallback = firstAllowedAppPath(user.permissions);
    return fallback && fallback !== location.pathname ? (
      <Navigate to={fallback} replace />
    ) : (
      <AccessDenied />
    );
  }
  if (any_permission && !any_permission.some((p) => user.permissions.includes(p))) {
    const fallback = firstAllowedAppPath(user.permissions);
    return fallback && fallback !== location.pathname ? (
      <Navigate to={fallback} replace />
    ) : (
      <AccessDenied />
    );
  }

  return <Outlet />;
}
